/**
 * ATTEST-INDEPENDENCE-MATRIX-1 (E.1) — which step caused this refusal, and the
 * one thing to do about it.
 *
 * The refusal panel is already good at *what* the guardrail was. It has been bad
 * at *where the CPA typed the thing that tripped it*: every gate fires on the
 * Build call from Step 5, so a wrong checkbox on Step 4 arrives as a red panel
 * on a different screen with no route back to the cause.
 *
 * This is deliberately a small static table rather than anything derived from
 * the payload. The mapping it encodes is a fact about *this wizard's layout* —
 * which of our five steps collects which input — and the server has no business
 * knowing that. The refusal content itself still comes entirely from the policy;
 * nothing here restates a reason, a rationale, or a deny-list entry.
 *
 * An unmapped code returns null and the panel simply omits the pointer. A wrong
 * signpost is worse than none.
 */

/** Mirrors the `STEPS` keys in `components/attest/WizardStepNav.tsx`. */
export type AttestStepKey =
  | 'classify' | 'scope' | 'details' | 'independence' | 'letter'

export interface RefusalGuidance {
  /**
   * The step that collects the input this code fires on, or `null` when no step
   * does (Bundle 2 item 3).
   *
   * `null` is not "we didn't map it" — an unmapped code is absent from the table
   * altogether. It means the table has been asked the question and the answer is
   * that nothing on these five screens can clear this: a firm profile that lives
   * outside the wizard, a policy file that failed to load. Those still get a
   * remedy sentence; what they must not get is a "Go to step 4" button that
   * lands somewhere unrelated and changes nothing.
   */
  step: AttestStepKey | null
  /** The step's own label, so the pointer reads the way the nav does. */
  stepLabel: string | null
  /** One line. What to change, not a restatement of what went wrong. */
  remedy: string
  /**
   * The DOM id of the control this code is usually about (ATTEST-UX-2 D2).
   *
   * Only set where the code names *one* field. A refusal that can come from
   * several places — an impairment (declared interest, or no attestation), a
   * denied assertion (which of five sentences?) — deliberately has none and
   * jumps to the step instead. Same rule as the table itself: a wrong signpost
   * is worse than none, and a cursor dropped in the wrong box is a signpost.
   */
  fieldAnchor?: string
}

const GUIDANCE: Record<string, RefusalGuidance> = {
  ATTEST_INDEPENDENCE_IMPAIRED: {
    step: 'independence',
    stepLabel: '4. Independence',
    remedy:
      'Resolve the impairment on the Independence step, or route this letter to an independent firm.',
  },
  ATTEST_ISSUER_NOT_ELIGIBLE: {
    step: 'independence',
    stepLabel: '4. Independence',
    remedy:
      'Fix the firm’s licence, peer review or back-office flag on the Independence step.',
    // The three eligibility controls sit together; the licence is the first,
    // so landing on it puts the whole block on screen.
    fieldAnchor: 'licensed-for-attest',
  },
  ATTEST_EVIDENCE_MISSING: {
    step: 'details',
    stepLabel: '3. Letter and statements',
    remedy:
      'Attach a source document to each flagged statement on the Letter and statements step.',
  },
  ATTEST_EVIDENCE_NOT_VERIFIED: {
    step: 'details',
    stepLabel: '3. Letter and statements',
    remedy:
      'Attach a document that actually supports the statement, reword the statement to what the attached document shows, or — where the statement offers it — tick that it is supported by records in your file.',
    // No anchor, deliberately. Four gates (kind, entity, period, preparer) can
    // each raise this code, and only the server knows which document failed
    // which. The step is the honest granularity.
    //
    // Three doors, not two, since item 4: the code now also fires when a
    // statement was never verified at all, and for the flag-and-attest tier the
    // CPA is entitled not to attach anything. Telling them to attach a document
    // they do not owe would be a dead end wearing a remedy's clothes.
  },
  ATTEST_INPUT_MISSING: {
    step: 'details',
    stepLabel: '3. Letter and statements',
    remedy: 'Supply the missing facts on the Letter and statements step.',
  },
  ATTEST_ASSERTION_DENIED: {
    step: 'details',
    stepLabel: '3. Letter and statements',
    remedy:
      'Reword or remove the flagged sentence — the safe alternative below is a CPA-permissible substitute.',
  },
  ATTEST_ASSURANCE_EXCEEDED: {
    step: 'classify',
    stepLabel: '1. Classify the request',
    remedy:
      'Lower the wording to what this engagement supports, or classify the request as a higher-assurance engagement.',
    fieldAnchor: 'assurance-level',
  },
  ATTEST_ENGAGEMENT_REFUSED: {
    step: 'classify',
    stepLabel: '1. Classify the request',
    remedy:
      'Re-answer the classification questions, or narrow what the requestor is asking for.',
  },
  ATTEST_REFUSED: {
    step: 'classify',
    stepLabel: '1. Classify the request',
    remedy: 'Revisit what is being asked for on the Classify step.',
    fieldAnchor: 'requested-items',
  },
  ATTEST_LETTER_TYPE_UNKNOWN: {
    step: 'classify',
    stepLabel: '1. Classify the request',
    remedy: 'Pick a letter type this deployment offers on the Classify step.',
    fieldAnchor: 'attest-letter-type',
  },

  // The two that no step can fix. Both used to fall through to `null` and
  // render as a bare wall — a red panel, a code, and no next move at all. That
  // reads as "you did something wrong and we won't say what", which is the
  // opposite of true in both cases.
  ATTEST_FIRM_PROFILE_MISSING: {
    // 403 from `core/attest/issuer_profile.py`: a letter is signed under the
    // firm legal name, CPA name and licence number held on the firm profile,
    // and this firm has no active one. Nothing in the wizard collects those —
    // deliberately, so nobody can hand-type credentials onto a letter.
    step: null,
    stepLabel: null,
    remedy:
      'Nothing on these steps can supply this. A letter is signed under the firm name, CPA name and licence number held on your firm profile — complete and activate that profile, then build the letter again.',
  },
  ATTEST_POLICY_INVALID: {
    // 500. The assertion policy failed to load, so no request was assessed at
    // all — this one is not about the CPA's letter and must not be phrased as
    // if it were.
    step: null,
    stepLabel: null,
    remedy:
      'This is a fault in the deployment, not in your request: the assertion policy did not load, so no letter can be issued for anyone until it is fixed. Report it to whoever administers this system and try again once they confirm it is back.',
  },
}

export function guidanceFor(errorCode: string): RefusalGuidance | null {
  return GUIDANCE[errorCode] ?? null
}

/**
 * Codes whose field is only knowable from the form's current state.
 *
 * "An input is missing" does not say *which* input, and the server does not
 * name a DOM id — but the wizard already derives exactly that list for the
 * asterisks and the preflight. So the anchor is the first still-missing field
 * matching the code's shape, and if nothing matches we fall through to the
 * static table and then to the step.
 */
const DYNAMIC_ANCHOR: Record<string, (fieldId: string) => boolean> = {
  ATTEST_INPUT_MISSING: id => id.startsWith('input-'),
  ATTEST_EVIDENCE_MISSING: id => id.startsWith('statement-evidence-'),
}

/**
 * Where to land for a refusal: the code's own field, or the first missing
 * field the code is about, or nothing (jump to the step).
 *
 * `missingFieldIds` is `missingRequiredFields(ctx).map(e => e.id)` — the ids
 * are the fields' DOM ids by construction (`requiredFields.tsx`).
 */
export function refusalAnchor(
  errorCode: string,
  missingFieldIds: string[] = [],
): string | undefined {
  const shape = DYNAMIC_ANCHOR[errorCode]
  const dynamic = shape ? missingFieldIds.find(shape) : undefined
  return dynamic ?? GUIDANCE[errorCode]?.fieldAnchor
}
